import { Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow } from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import { FC, useEffect, useRef } from 'react';
import T from 'src/components/content-manager/translator/T';
import { Colli } from 'src/components/elements/formats/Colli';
import { usePercentFormat } from 'src/core/hooks/format';
import VolumeDiscount from 'src/entities/volume-discount/VolumeDiscount';

const HIGHLIGHT_CLASS = 'VolumeDiscountTab-highlight';

const Root = styled(TableContainer)({
    overflowX: 'auto',
});

const StyledTable = styled(Table)(({ theme }) => ({
    [`& [class*="MuiTableCell-root"]:first-child`]: {
        position: 'sticky',
        left: 0,
        backgroundColor: theme.palette.grey[100],
        zIndex: 1,
    },
    '& [class*="MuiTableCell-root"]': {
        position: 'relative',
        width: '20%',
    },
    '& [class*="MuiTableHead-root"], & [class*="MuiTableFooter-root"]': {
        '& [class*="MuiTableCell-root"]': {
            borderWidth: 0,
        },
    },
    [`& .${HIGHLIGHT_CLASS}`]: {
        '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            borderColor: theme.palette.primary.main,
            borderStyle: 'solid',
            borderWidth: '0 1px',
        },
        '&.top:before': {
            borderTopWidth: '1px',
        },
        '&.bottom:before': {
            borderBottomWidth: '1px',
        },
    },
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightBold,
}));

interface VolumeDiscountTabProps {
    volumeDiscount: VolumeDiscount;
}

export const VolumeDiscountTab: FC<VolumeDiscountTabProps> = ({ volumeDiscount }) => {
    const formatPercent = usePercentFormat()();
    const topRowRef = useRef<HTMLTableRowElement>(null);

    useEffect(() => {
        if (topRowRef.current == null) return;

        topRowRef.current
            .querySelector(`.${HIGHLIGHT_CLASS}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'end' });
    }, []);

    return (
        <Root>
            <StyledTable>
                <TableHead>
                    <TableRow ref={topRowRef}>
                        <HeaderCell variant="head">
                            <T k="shop.volume_discount_discover.premium_levels.label" />
                        </HeaderCell>
                        {volumeDiscount.premiumLevelDiscounts.map((pld, iPremiumLevel) => (
                            <HeaderCell
                                align="right"
                                variant="head"
                                key={iPremiumLevel}
                                className={`top${
                                    volumeDiscount.activePremiumLevel === pld.premiumLevel ? ` ${HIGHLIGHT_CLASS}` : ''
                                }`}>
                                <T k={`shop.volume_discount_discover.premium_levels.${pld.premiumLevel}`} />
                            </HeaderCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {volumeDiscount.orderedMinQuantitySet.map(({ minQuantity, colli }) => (
                        <TableRow key={minQuantity}>
                            <TableCell>
                                <Colli amount={minQuantity} colliEntity={colli} />
                            </TableCell>
                            {volumeDiscount.getDiscounts(minQuantity).map((discountEntry, iPremiumLevel) => (
                                <TableCell
                                    align="right"
                                    key={iPremiumLevel}
                                    className={
                                        volumeDiscount.activePremiumLevel === (discountEntry?.premiumLevel ?? 0)
                                            ? HIGHLIGHT_CLASS
                                            : undefined
                                    }>
                                    {formatPercent(discountEntry?.value ?? 0)}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TableCell />
                        {volumeDiscount.premiumLevelDiscounts.map((pld, iPremiumLevel) => (
                            <TableCell
                                key={iPremiumLevel}
                                className={`bottom${
                                    volumeDiscount.activePremiumLevel === pld.premiumLevel ? ` ${HIGHLIGHT_CLASS}` : ''
                                }`}
                            />
                        ))}
                    </TableRow>
                </TableFooter>
            </StyledTable>
        </Root>
    );
};
